import { useCallback, useMemo } from 'react';
import _isEmpty from 'lodash/isEmpty';
import _includes from 'lodash/includes';
import _some from 'lodash/some';
import _reduce from 'lodash/reduce';
import { NavigationSection, NavigationCategory } from '../types';
import { useDrawerContext } from '../context';

type UseDrawerNavigationResult = {
  activeCategory: NavigationCategory;
  activeWithSelectionCategories: NavigationCategory[];
  navigationSections: NavigationSection[];
  shouldCategoryBeVisible: (categoryId: string) => boolean;
  handleDrawerNavigationChange: (newDrawerNavigation: NavigationSection[]) => void;
  handleActiveWithSelectionCategoriesChange: (filtersWithSelection: string[]) => void;
};

const useDrawerNavigation = (): UseDrawerNavigationResult => {
  const {
    isSearching,
    activeCategory,
    activeWithSelectionCategories,
    navigationSections,
    navigationCategories,
    setActiveWithSelectionCategories,
    setNavigationSections,
  } = useDrawerContext();

  const shouldCategoryBeVisible = (categoryValue: string): boolean => {
    return activeCategory.value === categoryValue || isSearching;
  };

  const handleDrawerNavigationChange = useCallback(
    (newDrawerNavigation: NavigationSection[]): void => {
      if (_isEmpty(navigationSections)) {
        setNavigationSections(newDrawerNavigation);
      }
    },
    [navigationSections]
  );

  const handleActiveWithSelectionCategoriesChange = useCallback(
    (filtersWithSelection: string[]): void => {
      if (filtersWithSelection && navigationCategories.length > 0) {
        const newActiveWithSelectionCategories = _reduce(
          navigationCategories,
          (acc, category) => {
            const doAnyCategoryFiltersHaveSelection = _some(category?.options, (filter) =>
              _includes(filtersWithSelection, filter.value)
            );

            if (doAnyCategoryFiltersHaveSelection) {
              return [...acc, category];
            }

            return acc;
          },
          []
        );

        setActiveWithSelectionCategories(newActiveWithSelectionCategories);
      }
    },
    [navigationCategories]
  );

  return useMemo(
    () => ({
      activeCategory,
      activeWithSelectionCategories,
      navigationSections,
      shouldCategoryBeVisible,
      handleDrawerNavigationChange,
      handleActiveWithSelectionCategoriesChange,
    }),
    [
      activeCategory,
      activeWithSelectionCategories,
      navigationSections,
      shouldCategoryBeVisible,
      handleDrawerNavigationChange,
      handleActiveWithSelectionCategoriesChange,
    ]
  );
};

export default useDrawerNavigation;
